/**
 * The Providers tab's read-only "Image reading model" line
 * (persona ADR-018).
 *
 * There is no picker and no setter: the image-reading model is one stack-wide
 * platform choice. Presentation only — the caller supplies the query result, so
 * this renders in a functional story with no backend.
 *
 * Two distinct warnings, and conflating them would misdirect the fix:
 *   - INELIGIBLE — the model itself cannot read images (label or adapter). No
 *     key changes that.
 *   - KEY POLICY — the model is fine, but this account's own-keys-only policy
 *     for that provider has no key stored. Shown ONLY when the backend says so
 *     — an account holding a matching key gets no message at all.
 */

import type { VisionModelInfo } from '@localagent/sdk';
import { ModelCapabilityPills } from './ModelCatalogCells';

export interface VisionModelNoticeProps {
  info?: VisionModelInfo;
  isLoading?: boolean;
  error?: Error | null;
}

export function VisionModelNotice({ info, isLoading, error }: VisionModelNoticeProps) {
  if (error) {
    return (
      <div
        data-testid="vision-model-notice"
        data-state="error"
        className="rounded-md bg-destructive/10 p-3 text-sm text-destructive"
      >
        Could not resolve the image reading model: {error.message}
      </div>
    );
  }

  if (isLoading || !info) {
    return (
      <div
        data-testid="vision-model-notice"
        data-state="loading"
        className="rounded-md bg-card p-3 text-sm text-muted-foreground"
      >
        Resolving the image reading model…
      </div>
    );
  }

  return (
    <div
      data-testid="vision-model-notice"
      data-state={info.eligible ? 'eligible' : 'ineligible'}
      data-key-policy-blocks={String(info.key_policy_blocks)}
      className="rounded-md bg-card p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">Image reading model</h3>
        <span
          data-testid="vision-model-name"
          className="font-mono text-xs text-muted-foreground"
        >
          {info.model_name}
        </span>
        <span data-testid="vision-model-provider" className="text-xs text-muted-foreground">
          on {info.provider_type}
        </span>
        <ModelCapabilityPills
          flags={{
            supports_tools: info.supports_tools,
            supports_vision: info.supports_vision,
          }}
        />
      </div>

      <p className="mt-1 text-xs text-muted-foreground">
        {info.display_name ? `${info.display_name} — ` : ''}
        Every agent reads attached images through this model. It is a platform
        choice and cannot be changed here.
      </p>

      {!info.eligible && (
        <p
          data-testid="vision-model-ineligible"
          className="mt-2 text-xs text-destructive"
        >
          {info.ineligible_reason ??
            'This model cannot read images, so image reading is unavailable.'}
        </p>
      )}

      {info.key_policy_blocks && (
        <p data-testid="vision-model-key-policy" className="mt-2 text-xs text-amber-600 dark:text-amber-400">
          This account requires its own keys for {info.provider_type} and none is
          stored, so image reading cannot run until a {info.provider_type} key is
          added below.
        </p>
      )}
    </div>
  );
}
